import React, { useState, useEffect } from 'react';
import { Form, Input, Row, Col, Button, Space, Modal } from 'antd';
import { apiRequest } from '../../../../service/apiRequest';
import { showSuccess, showError } from '../../../../components/errorHandler';

interface DesignationModalProps {
  visible: boolean;
  onCancel: () => void;
  onCreate: (data: any) => void; // Callback after successful submit
  initialValues?: {
    designation_id?: number;
    designation_name?: string;
  } | null;
  isEditMode?: boolean;
}

const DesignationModal: React.FC<DesignationModalProps> = ({
  visible,
  onCancel,
  onCreate,
  initialValues = null,
  isEditMode = false,
}) => {
  const [form] = Form.useForm();
  const [isModalClose, setIsModalClose] = useState(true);

  // Determine if submit button should be disabled
  const isSubmitDisabled = () => {
    const errors = form.getFieldsError();
    return errors.some(({ errors }) => errors.length > 0);
  };

  // Handle form submit
  const handleAddDesignation = async (values: any) => {
    try {
      const dataVal = {
        designation_name: values.designation_name,
        ...(initialValues?.designation_id && { designation_id: initialValues.designation_id }),
      };

      const url = isEditMode ? 'edit-designation' : 'add-designation';
      const method = isEditMode ? 'put' : 'post';

      const data = await apiRequest({
        method,
        url,
        data: dataVal,
      });

      onCreate(data); // Notify parent about success
      showSuccess(
        isEditMode
          ? 'Designation updated successfully!'
          : 'Designation created successfully!'
      );

      form.resetFields();

      if (isModalClose) {
        onCancel(); // Close modal if Submit button clicked
      }
    } catch (error) {
      showError(`Failed to ${isEditMode ? 'update' : 'create'} designation`);
    }
  };

  // Reset or populate form when modal opens or mode changes
  useEffect(() => {
    if (visible && isEditMode && initialValues) {
      form.setFieldsValue({
        designation_name: initialValues.designation_name,
      });
    } else if (visible) {
      form.resetFields();
    }
  }, [visible, isEditMode, initialValues, form]);

  return (
    <Modal
      title={isEditMode ? 'Edit Designation' : 'Add New Designation'}
      visible={visible}
      onCancel={onCancel}
      footer={null}
      width="50%"
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleAddDesignation}
        autoComplete="off"
      >
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="designation_name"
              label={
                <span>
                  Designation&nbsp;
                  <span style={{ fontSize: '12px', color: '#888' }}>
                    (Max 255 characters)
                  </span>
                </span>
              }
              rules={[
                { required: true, message: 'Please enter the Designation' },
                { max: 255, message: 'Maximum 255 characters allowed' },
              ]}
            >
              <Input showCount maxLength={255} placeholder="e.g., Manager" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Space>
            <Button onClick={onCancel}>Cancel</Button>
            {!isEditMode && (
              <Button
                type="primary"
                htmlType="submit"
                onClick={() => setIsModalClose(false)}
                disabled={isSubmitDisabled()}
              >
                Submit & Create New Designation
              </Button>
            )}
            <Button
              type="primary"
              htmlType="submit"
              onClick={() => setIsModalClose(true)}
              disabled={isSubmitDisabled()}
            >
              Submit
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default DesignationModal;
